import React, { useRef, useEffect, useCallback, useState } from "react";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch-hooks-web";
import ListView from "./views/listView";
import DriveContext from "./driveContext";
import { getMediaType } from "../../utils/mediaUtils";

function SearchResultsView({
	onSelect,
	onDblClick,
	acceptType,
	driveComponentId,
	scrollParentRef,
	mode,
	onSearchImageResultsUpdate,
	showAllLabels
}) {
	const { hits, showMore, isLastPage } = useInfiniteHits();
	const { results } = useInstantSearch();
	const totalHits = results?.nbHits ?? 0;
	const observerRef = useRef(null);
	const localScrollRef = useRef(null);

	// Sync scroll ref
	useEffect(() => {
		if (scrollParentRef?.current != null) {
			scrollParentRef.current = localScrollRef.current;
		}
	}, [scrollParentRef]);

	// Auto-load when sentinel in view
	const sentinelRef = useCallback(
		(node) => {
			if (observerRef.current) { observerRef.current.disconnect(); }
			observerRef.current = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting && !isLastPage) {
					showMore();
				}
			});
			if (node) { observerRef.current.observe(node); }
		},
		[showMore, isLastPage]
	);

	// Map Algolia hits into drive-compatible files
	const mappedHits = hits.map(hit => {
		const path = hit.path || "";
		const pathParts = path.split("/");
		const fileName = pathParts[pathParts.length - 1] || "";
		const ext = fileName.includes(".") ? fileName.split(".").pop().toUpperCase() : "";
		const guid = hit.guid || hit.objectID || "";

		return {
			type: "file",
			ext,
			name: fileName,
			path,                 // now using path instead of guid
			_path: pathParts,
			isFile: true,
			fileName,
			lastUpdatedBy: hit.lastUpdatedBy || "",
			labels: hit.labels || [],
			private: hit.private || false,
			alias: hit.alias || false,
			metaData: hit.metaData || {},
			guid,                // now a true GUID, separate from path
			revision: hit.revision || 1,
			orgId: hit.orgId || "",
			createdAt: hit.createdAt || new Date().toISOString(),
			scope: hit.scope || "",
			searchResult: true
		};
	});

	const prevImageGuidsRef = useRef([]);

	useEffect(() => {
		if (typeof onSearchImageResultsUpdate === "function") {
			const allowedMediaTypes = ["image", "video", "audio", "pdf"];

			const mediaFiles = mappedHits.filter(
				node => node.type === "file" && allowedMediaTypes.includes(getMediaType(node.ext))
			);

			const newGuids = mediaFiles.map(file => file.guid).sort();
			const prevGuids = prevImageGuidsRef.current;

			const changed =
				newGuids.length !== prevGuids.length ||
				newGuids.some((guid, i) => guid !== prevGuids[i]);

			if (changed) {
				prevImageGuidsRef.current = newGuids;
				onSearchImageResultsUpdate(mediaFiles);
			}
		}
	}, [mappedHits, onSearchImageResultsUpdate]);


	const fakeHierarchy = {
		type: "folder",
		name: "Search Results",
		root: true,
		guid: "search-root",
		children: mappedHits,
		allowDelete: false,
		allowMoveCopyRename: false,
		allowCreate: false
	};

	return (
		<>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
				<div>Showing {hits.length} of {totalHits.toLocaleString()} results</div>
			</div>

			<div className="drive-tree-wrapper">
				<div className="drive-tree" style={{ height: "100%", overflowY: "auto" }} ref={localScrollRef}>
					<DriveContext.Provider value={{ driveComponentId }}>
						<ListView
							data={fakeHierarchy}
							onSelect={onSelect}
							onDblClick={onDblClick}
							mode={mode}
							accept={acceptType}
							scrollParentRef={scrollParentRef}
							driveComponentId={driveComponentId}
							showAllLabels={showAllLabels}
						/>
					</DriveContext.Provider>

					{/* Sentinel to trigger infinite scroll */}
					<div ref={sentinelRef} style={{ height: "1px" }} />
				</div>
			</div>

		</>
	);
}

export default SearchResultsView;
